// Multi-accent speech recognition service for diagnostic purposes
// Tests the same audio against multiple accent models simultaneously

export interface AccentConfig {
  code: string;
  name: string;
  flag: string;
}

export const ACCENT_CONFIGS: AccentConfig[] = [
  { code: 'en-US', name: 'English (US)', flag: '🇺🇸' },
  { code: 'en-GB', name: 'English (UK)', flag: '🇬🇧' },
  { code: 'en-AU', name: 'English (Australia)', flag: '🇦🇺' },
  { code: 'en-CA', name: 'English (Canada)', flag: '🇨🇦' },
  { code: 'en-IN', name: 'English (India)', flag: '🇮🇳' },
  { code: 'en-NZ', name: 'English (New Zealand)', flag: '🇳🇿' },
  { code: 'en-ZA', name: 'English (South Africa)', flag: '🇿🇦' },
  { code: 'en-IE', name: 'English (Ireland)', flag: '🇮🇪' },
  { code: 'en-SG', name: 'English (Singapore)', flag: '🇸🇬' },
  { code: 'en-PH', name: 'English (Philippines)', flag: '🇵🇭' },
];

export interface AccentRecognitionResult {
  accent: string;
  flag: string;
  transcript: string;
  confidence: number;
  status: 'pending' | 'processing' | 'complete' | 'error';
  error?: string;
}

export class MultiAccentRecognitionService {
  private recognizers: Map<string, any> = new Map();
  private audioBlob: Blob | null = null;
  private results: Map<string, AccentRecognitionResult> = new Map();
  
  constructor() {
    this.initializeRecognizers();
  }
  
  private initializeRecognizers() {
    const SpeechRecognition = (window as any).SpeechRecognition || 
                             (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.error('Speech Recognition API not supported');
      return;
    }
    
    // Create a recognizer for each accent
    ACCENT_CONFIGS.forEach(config => {
      try {
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.maxAlternatives = 3;
        recognition.lang = config.code;
        
        this.recognizers.set(config.code, recognition);
        
        // Initialize result
        this.results.set(config.code, {
          accent: config.name,
          flag: config.flag,
          transcript: '',
          confidence: 0,
          status: 'pending'
        });
      } catch (error) {
        console.error(`Failed to create recognizer for ${config.code}:`, error);
      }
    });
  }
  
  // Process audio with all accent models
  async processAudioWithAllAccents(audioBlob: Blob): Promise<Map<string, AccentRecognitionResult>> {
    this.audioBlob = audioBlob;
    
    // Reset all results
    ACCENT_CONFIGS.forEach(config => {
      this.results.set(config.code, {
        accent: config.name,
        flag: config.flag,
        transcript: '',
        confidence: 0,
        status: 'pending'
      });
    });
    
    // Process each accent sequentially (parallel causes conflicts)
    for (const config of ACCENT_CONFIGS) {
      await this.processWithAccent(config.code);
      // Small delay between recognitions to avoid API rate limits
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    return this.results;
  }
  
  private async processWithAccent(accentCode: string): Promise<void> {
    const recognizer = this.recognizers.get(accentCode);
    const config = ACCENT_CONFIGS.find(c => c.code === accentCode);
    
    if (!recognizer || !config || !this.audioBlob) {
      this.results.set(accentCode, {
        accent: config?.name || accentCode,
        flag: config?.flag || '',
        transcript: '',
        confidence: 0,
        status: 'error',
        error: 'Recognizer not available'
      });
      return;
    }
    
    // Update status to processing
    this.results.set(accentCode, {
      ...this.results.get(accentCode)!,
      status: 'processing'
    });
    
    return new Promise((resolve) => {
      // Convert blob to audio element for playback
      const audio = new Audio();
      const audioUrl = URL.createObjectURL(this.audioBlob!);
      audio.src = audioUrl;
      
      // Set up recognition handlers
      recognizer.onresult = (event: any) => {
        let bestTranscript = '';
        let bestConfidence = 0;
        
        if (event.results.length > 0) {
          const result = event.results[0];
          
          // Check all alternatives for best confidence
          for (let i = 0; i < result.length; i++) {
            const alternative = result[i];
            const confidence = alternative.confidence || 0.5;
            
            if (confidence > bestConfidence) {
              bestTranscript = alternative.transcript;
              bestConfidence = confidence;
            }
          }
        }
        
        this.results.set(accentCode, {
          accent: config.name,
          flag: config.flag,
          transcript: bestTranscript,
          confidence: bestConfidence,
          status: 'complete'
        });
        
        URL.revokeObjectURL(audioUrl);
        resolve();
      };
      
      recognizer.onerror = (event: any) => {
        console.error(`Recognition error for ${accentCode}:`, event.error);
        
        this.results.set(accentCode, {
          accent: config.name,
          flag: config.flag,
          transcript: '',
          confidence: 0,
          status: 'error',
          error: event.error
        });
        
        URL.revokeObjectURL(audioUrl);
        resolve();
      };
      
      recognizer.onnomatch = () => {
        this.results.set(accentCode, {
          accent: config.name,
          flag: config.flag,
          transcript: '(no match)',
          confidence: 0,
          status: 'complete'
        });
        
        URL.revokeObjectURL(audioUrl);
        resolve();
      };
      
      recognizer.onend = () => {
        // Ensure we resolve even if no result was received
        const currentResult = this.results.get(accentCode);
        if (currentResult?.status === 'processing') {
          this.results.set(accentCode, {
            accent: config.name,
            flag: config.flag,
            transcript: '(no speech detected)',
            confidence: 0,
            status: 'complete'
          });
        }
        
        URL.revokeObjectURL(audioUrl);
        resolve();
      };
      
      // Start recognition with audio playback
      try {
        // For Web Speech API, we need to use microphone input
        // So we'll simulate by playing the audio and capturing it
        // This is a limitation of the Web Speech API
        
        // Alternative: Use a mock transcript for now
        // In production, you'd use Google Cloud Speech-to-Text API
        recognizer.start();
        
        // Simulate audio input (this won't work perfectly with Web Speech API)
        setTimeout(() => {
          recognizer.stop();
        }, 3000); // Stop after 3 seconds
        
      } catch (error) {
        console.error(`Failed to start recognition for ${accentCode}:`, error);
        
        this.results.set(accentCode, {
          accent: config.name,
          flag: config.flag,
          transcript: '',
          confidence: 0,
          status: 'error',
          error: 'Failed to start'
        });
        
        URL.revokeObjectURL(audioUrl);
        resolve();
      }
    });
  }
  
  // Get current results
  getResults(): Map<string, AccentRecognitionResult> {
    return new Map(this.results);
  }
  
  // Get best performing accent
  getBestAccent(): string | null {
    let bestAccent: string | null = null;
    let bestConfidence = 0;
    
    this.results.forEach((result, accentCode) => {
      if (result.status === 'complete' && result.confidence > bestConfidence) {
        bestConfidence = result.confidence;
        bestAccent = accentCode;
      }
    });
    
    return bestAccent;
  }
  
  // Clean up
  destroy() {
    this.recognizers.forEach(recognizer => {
      try {
        recognizer.abort();
      } catch (e) {
        // Ignore
      }
    });
    this.recognizers.clear();
    this.results.clear();
  }
}

// Singleton instance
let multiAccentServiceInstance: MultiAccentRecognitionService | null = null;

export function getMultiAccentService(): MultiAccentRecognitionService {
  if (!multiAccentServiceInstance) {
    multiAccentServiceInstance = new MultiAccentRecognitionService();
  }
  return multiAccentServiceInstance;
}